import type { AppId } from 'constants/known-apps';
import knownApps, {
  adminOnlyApps,
  betaApps,
  customOptionApps,
  GOOGLE_CALENDAR,
  LINUX,
  MAC,
  MEMORY_BROWSER_EXTENSION,
  MONDAY,
  TIMELY_GPS_TRACKER,
  WINDOWS,
} from 'constants/known-apps';
import { appIcons } from 'constants/known-apps.icons';
import type { FactoryApp, FactoryAppAccount } from 'factories/apps';
import { appAccountFactory, appFactory, reduceAppByScope } from 'factories/apps';
import { importEntityAdapter } from 'redux/modules/apps.reducer';
import { ImportStatus } from 'redux/modules/apps.types';
import { xhrStatusUIState } from 'redux/reusable/xhrStatus';
import type { IState } from 'redux/state';
import { createSelector } from 'reselect';
import type { Features } from 'types/account';
import type { App as AppType } from 'types/apps';
import { ScopeTypes } from 'types/apps';
import {
  getAppDeleteWarning,
  getAppImportDetails,
  getAppSubtitle,
  getAppsFilteredByName,
} from 'utils/apps';
import { notNull, orderBy } from 'utils/typesafe';
import {
  getCurrentImportAccountId,
  getCurrentImportStatus,
  getIsImporting,
} from '../modules/integrations.selector';
import {
  getAccountExperiments,
  getAccountFeatures,
  getAreAzureADIntegrationEnabled,
  getArePremiumIntegrationsEnabled,
  getIsActiveTrial,
} from './accounts';
import { getIsUserAdmin, getUserTimeFormat } from './user';

const getUserEmail = (state: IState) => state.user.email;
export const getAppsDraft = (state: IState) => state.apps.draft;
const getAppsFilter = (state: IState) => state.apps.filter;
const getAppsEntities = (state: IState) => state.apps.appsEntities;
const getAppsResult = (state: IState) => state.apps.appsResult;
const readAppAccounts = (state: IState) => state.apps.accounts;
const getAppSubscriptions = (state: IState) => state.apps.subscriptions;

const readIsShowingAppsListModal = (state: IState) => state.apps.showAppsListModal;
const readIsShowingAppEditor = (state: IState) => !!state.apps.appEditorId;
const readIsShowingAppImport = (state: IState) => !!state.integrations.importAccountId;
const readIsShowingUnassignedUsersModal = (state: IState) =>
  state.integrations.showUnassignedUsersModal;
const readOnboardingList = () => knownApps.onboardingList;
const readMemoryAppsList = () => knownApps.memoryApps;
const readCurrentInvoiceId = (state: IState) => state.invoice.currentInvoiceId;

const readXhrStatus = (state: IState) => state.apps.xhrStatus;

export const getAppEditorId = (state: IState) => state.apps.appEditorId;
export const getAppAccountEditorId = (state: IState) => state.apps.appAccountEditorId;
export const getIsShowingAppAccountEditor = (state: IState) => !!state.apps.appAccountEditorId;

export const getIsFetchingApps = createSelector(
  [readXhrStatus],
  xhrStatus => xhrStatusUIState(xhrStatus, 'apps').isLoading
);

export const getAppAccounts = readAppAccounts;

// This is used to keep legacy compatibility with modals in AppRoute,
// BUT it's a silly solve.
// It should be moved out of AppRoute, and use <SuperModal /> or something instead.

export const getAppModalStates = createSelector(
  [
    readIsShowingAppsListModal,
    readIsShowingAppEditor,
    readIsShowingAppImport,
    readIsShowingUnassignedUsersModal,
    readCurrentInvoiceId,
  ],
  (showAppsListModal, showAppEditor, showAppImport, showUnassignedUsersModal, currentInvoiceId) => {
    return {
      showAppsListModal,
      showAppEditor,
      showAppImport,
      showUnassignedUsersModal,
      isShowingInvoice: !!currentInvoiceId,
    };
  }
);

const getApps = createSelector(
  [getAppsEntities, getAppsResult],
  (entities, result): Array<AppType> => {
    return result.map(id => entities[id]);
  }
);

const firstPartyApps: Array<any> = [
  MAC,
  WINDOWS,
  LINUX,
  MEMORY_BROWSER_EXTENSION,
  TIMELY_GPS_TRACKER,
];

export const getEnhancedApps = createSelector(
  [
    getApps,
    getUserTimeFormat,
    getArePremiumIntegrationsEnabled,
    getAreAzureADIntegrationEnabled,
    getAccountFeatures,
  ],
  (apps, timeFormat, canUsePremiumIntegrations, canUseAzureADIntegration, accountFeatures) => {
    // Sort first party apps before others
    const sortedApps = orderBy(apps, app => {
      const index = firstPartyApps.indexOf(app.id);
      return index === -1 ? Number.MAX_VALUE : index;
    });

    return sortedApps.map(app =>
      appFactory(app, {
        timeFormat,
        canUsePremiumIntegrations,
        canUseAzureADIntegration,
        accountFeatures,
      })
    );
  }
);

export const getOnboardingApps = createSelector(
  [getEnhancedApps, readOnboardingList, getAccountExperiments],
  (apps, onboardingList, experiments): Array<FactoryApp> => {
    const appsList = apps || [];

    if (experiments['onboarding']) {
      return appsList.filter(app => {
        const isTracker = (knownApps.trackerApps as ReadonlyArray<string>).includes(app.id);
        const isAppInUserScope = app.scope === 'user';
        return !isTracker && isAppInUserScope;
      });
    } else {
      return onboardingList
        .map(name => appsList.find(app => app.id === name) || null)
        .filter(notNull);
    }
  }
);

export const getMemorySourceApps = createSelector(
  [getEnhancedApps, readMemoryAppsList],
  (apps, memoryApps) => apps.filter(app => (memoryApps as ReadonlyArray<string>).includes(app.id))
);

export const getConnectedApps = createSelector([getEnhancedApps], apps =>
  apps.filter(app => app.integrated)
);

export const getConnectedAppIds = createSelector([getConnectedApps], (apps): Array<AppId> => {
  return apps.map(app => app.id);
});

export const getIsMemoryAppConnected = createSelector([getConnectedAppIds], (appIds): boolean => {
  return appIds.some(id => {
    return id === MAC || id === WINDOWS || id === LINUX || id === MEMORY_BROWSER_EXTENSION;
  });
});

export const getConnectedMemorySources = createSelector(
  [getMemorySourceApps, getConnectedApps],
  (apps, connectedApps) => apps.filter(app => connectedApps.includes(app))
);

export const getDisconnectedMemorySources = createSelector(
  [getMemorySourceApps, getConnectedApps],
  (apps, connectedApps) => apps.filter(app => !connectedApps.includes(app))
);

export const getHasConnectedMemorySource = createSelector(
  [getConnectedMemorySources],
  apps => apps.length > 0
);

export const getHasConnectedTrackerApp = createSelector([getConnectedAppIds], appIds =>
  appIds.some(appId => (knownApps.trackers as ReadonlyArray<string>).includes(appId))
);

export const getHasConnectedNonTrackerApp = createSelector([getConnectedAppIds], appIds => {
  return appIds.some(appId => !(knownApps.trackers as ReadonlyArray<string>).includes(appId));
});

export const mergedMemoryAppName = 'Memory';
export const mergedMemoryAppDescription = 'Track time spent in apps on the web and desktop.';

export const getToolbarApps = createSelector([getMemorySourceApps], apps => {
  return apps.reduce<Array<FactoryApp>>((toolbarApps, app) => {
    if (!app.integrated) return toolbarApps;
    toolbarApps.push(app);
    return toolbarApps;
  }, []);
});

// AppsList props selectors

export const getAppsForAppsList = createSelector(
  [getConnectedMemorySources, getDisconnectedMemorySources],
  (connectedApps, disconnectedApps) => ({
    connectedApps,
    disconnectedApps,
  })
);

export const getAppsListData = createSelector([getEnhancedApps, getAppsFilter], (apps, filter) => {
  const filteredApps = getAppsFilteredByName(apps, filter.search);

  const disconnectedApps = filteredApps
    .filter(({ ui }) => ui.isDisconnected)
    .reduce(reduceAppByScope, {
      [ScopeTypes.account]: [],
      [ScopeTypes.user]: [],
    });

  const enabledApps = filteredApps
    .filter(({ ui }) => ui.isEnabled && !ui.isDisconnected)
    .reduce(reduceAppByScope, {
      [ScopeTypes.account]: [],
      [ScopeTypes.user]: [],
    });

  const notEnabledApps = filteredApps
    .filter(({ ui }) => !ui.isEnabled)
    .reduce(reduceAppByScope, {
      [ScopeTypes.account]: [],
      [ScopeTypes.user]: [],
    });

  return {
    disconnectedApps,
    enabledApps,
    notEnabledApps,
    ui: {
      filter: filter.search,
      isLoading: !filteredApps.length,
      showEmptyScreen: !!filteredApps.length,
    },
  };
});

export const readAppAccountsById = (state: IState, props: { id: number }) =>
  state.apps.accounts[props.id];

export const getAppAccountsById = readAppAccountsById;
export const makeGetAppAccountsById = () => getAppAccountsById;

// AppEditor props selectors

export const getAccountsForAppEditor = createSelector(
  [getAppEditorId, getAppAccounts, getAppSubscriptions, getUserEmail, getUserTimeFormat],
  (id, accounts, subscriptions, userEmail, timeFormat) => {
    if (id == null || !accounts[id]) {
      return [];
    }

    return accounts[id].map(account => {
      let activeSubscriptions;

      // Subscriptions might be loading at this point.
      if (subscriptions[account.id]) {
        activeSubscriptions = subscriptions[account.id].filter(
          subscription => subscription.integrated
        );
      }

      return appAccountFactory({
        account,
        userEmail,
        subscriptions: activeSubscriptions,
        timeFormat,
      });
    });
  }
);

export const readImportWizardStatus = (state: IState) => state.apps.import.status;

export const getImportWizardStatus = readImportWizardStatus;

export const getAppEditorData = createSelector(
  [
    getAppsEntities,
    getAppEditorId,
    getAccountsForAppEditor,
    getIsImporting,
    getAppModalStates,
    getCurrentImportStatus,
    getCurrentImportAccountId,
    getIsUserAdmin,
    getArePremiumIntegrationsEnabled,
    getAreAzureADIntegrationEnabled,
    getIsActiveTrial,
    getAccountFeatures,
    getHasConnectedTrackerApp,
    getIsShowingAppAccountEditor,
    getImportWizardStatus,
    (state: IState) => {
      const xhrUiState = xhrStatusUIState(state.apps.xhrStatus, 'accounts');
      return xhrUiState.hasInitialized && !xhrUiState.isLoading;
    },
  ],
  (
    apps,
    appId,
    accounts,
    _isImporting,
    appModalStates,
    importStatus,
    currentImportAccountId,
    isAdmin,
    canUsePremiumIntegrations,
    canUseAzureADIntegration,
    isActiveTrial,
    accountFeatures,
    hasConnectedTrackerApp,
    isShowingAppAccountEditor,
    importWizardStatus,
    hasLoadedAccounts
  ) => {
    if (appId == null) {
      return {
        appId: null,
        accounts,
      };
    }
    const app = apps[appId];
    const {
      id,
      display_name: displayName,
      can_connect,
      feature_requirements,
      provider,
      premium,
      summary,
      platforms,
      authorize_url,
      logo_url,
      scope,
      html,
      support_url,
    } = app;

    const { mobileApps, trackerApps } = knownApps;
    const isMobileApp = (mobileApps as ReadonlyArray<string>).includes(id);
    const isTracker = (trackerApps as ReadonlyArray<string>).includes(id);
    const showAppStoreLink = isMobileApp && platforms.includes('ios');
    const showPlayStoreLink = isMobileApp && platforms.includes('android');
    const showSettingsButton =
      id === 'google_calendar' ||
      id === 'office365' ||
      id === 'jira' ||
      customOptionApps.includes(id);
    // accountType very generic for now, should probably be a factory function or similar
    // as we add more integrations
    const accountType: 'calendar' | 'workspace' =
      id === 'google_calendar' || id === 'office365' ? 'calendar' : 'workspace';
    const showImportButton = id === 'toggl' || id === 'harvest';
    const deletePrompt = `Are you sure? ${getAppDeleteWarning(id, accountType, displayName)}`;

    // TODO: This way of special casing invoices is (probably?) obsolete with the new categorization
    // and handling of integrations, but need to check with backend/success before removing this
    const isInvoicesFeature = !!feature_requirements?.includes('invoices');

    const accountLevelIntegration = scope === ScopeTypes.account || isInvoicesFeature;
    const isAdminOnlyApp = (adminOnlyApps as ReadonlyArray<string>).includes(id);

    const isMissingRequiredFeature = !!feature_requirements?.some(
      (key: keyof Features) => !accountFeatures[key]
    );

    const isImporting = _isImporting || importWizardStatus === ImportStatus.IN_PROGRESS;

    // Non-admins cannot connect admin-only apps or invoice apps
    const canConnect =
      can_connect &&
      !isTracker &&
      !(isInvoicesFeature && !isAdmin) &&
      !(isAdminOnlyApp && !isAdmin);

    return {
      appId,
      id,
      premium,
      accounts,
      displayName,
      importStatus,
      currentImportAccountId,
      isImporting,
      subtitle: getAppSubtitle(id, provider),
      summary,
      html: !!html?.summary.length || !!html?.uninstall.length ? html : null,
      scope,
      logoUrl: appIcons[id] || logo_url,
      noAccountsMessage: `No ${displayName} accounts connected.`,
      url: authorize_url,
      supportUrl: support_url,
      ui: {
        ...appModalStates,
        accountType,
        pluralisedAppType: accountType === 'calendar' ? 'calendars' : 'workspaces',
        deletePrompt,
        details: getAppImportDetails(id),
        isImporting,
        showImportWizard: importWizardStatus === ImportStatus.INITIALIZED,
        showContactAdminMessage: (accountLevelIntegration || isAdminOnlyApp) && !isAdmin,
        showAppStoreLink,
        showPlayStoreLink,
        showMobileLinks: showAppStoreLink || showPlayStoreLink,
        showConnectButton: canConnect,
        showMacTrackerInstructions: id === MAC,
        showLinuxTrackerInstructions: id === LINUX,
        showWindowsTrackerInstructions: id === WINDOWS,
        showChromeTrackerInstructions: id === MEMORY_BROWSER_EXTENSION,
        showMondayComInstructions:
          id === MONDAY && (!app.integrated || (hasLoadedAccounts && !accounts.length)),
        showSettingsButton,
        showImportButton,
        isLockedFeature:
          ((app.premium || isInvoicesFeature) && !canUsePremiumIntegrations) ||
          (app.unlimited_plus && !canUseAzureADIntegration) ||
          isMissingRequiredFeature,
        isLockedFeatureAfterTrial:
          isActiveTrial && (app.premium || app.unlimited_plus || isInvoicesFeature),
        isMissingRequiredFeature,
        hasConnectedTrackerApp,
        isShowingAppAccountEditor,
        isBeta: Object.hasOwn(betaApps, id),
      },
    };
  }
);

const getIsSavingAppAccount = createSelector(
  [readXhrStatus],
  xhrStatus => xhrStatus.saving.findIndex(xhr => xhr.key === 'accounts') !== -1
);

export const getHasAppAccountOptions = createSelector(
  [getAppAccounts, getAppEditorId, getAppAccountEditorId],
  (appAccounts, appId, appAccountId) => {
    return !!appId && !!appAccounts[appId]?.find(a => a.id === appAccountId)?.options;
  }
);

export const getAppAccountEditorData = createSelector(
  [
    getAppsDraft,
    getAppEditorData,
    getAppAccountEditorId,
    getIsSavingAppAccount,
    getHasAppAccountOptions,
  ],
  (draft, editorData, editorId, isSavingAppAccount, hasOptions) => {
    // casting here as any other type of narrowing fails
    const accounts = editorData.accounts as Array<FactoryAppAccount>;
    const account = accounts.find(account => account.id === editorId);
    const selectedSubscriptions = draft.subscriptions?.filter(sub => !!sub.integrated) || [];
    return {
      subscriptions: draft.subscriptions,
      account,
      allSubscriptionsUnselected: selectedSubscriptions.length === 0,
      isSavingAppAccount,
      hasOptions,
      ...editorData,
    };
  }
);

export const getHasDisconnectedApps = createSelector([getEnhancedApps], apps => {
  const disconnectedApps = apps.filter(app => app.ui.isDisconnected);
  return disconnectedApps.length > 0;
});

export interface AppImports {
  connected: Array<FactoryApp>;
  available: Array<FactoryApp>;
  disconnected: Array<FactoryApp>;
}

export const getAppsWithProjectImportSupport = createSelector([getEnhancedApps], apps => {
  const relevantApps = apps.filter(project => project.importSupport?.includes('Projects'));

  return {
    connected: relevantApps.filter(app => app.ui.isEnabled && !app.ui.isDisconnected),
    available: relevantApps.filter(app => !app.ui.isEnabled),
    disconnected: relevantApps.filter(app => app.ui.isDisconnected),
  };
});

export const getAppsWithUserImportSupport = createSelector([getEnhancedApps], apps => {
  const relevantApps = apps.filter(project => project.importSupport?.includes('Users'));

  return {
    connected: relevantApps.filter(app => app.ui.isEnabled && !app.ui.isDisconnected),
    available: relevantApps.filter(app => !app.ui.isEnabled),
    disconnected: relevantApps.filter(app => app.ui.isDisconnected),
  };
});

export const getAppAccountOptions = createSelector(
  [getAppsEntities, getAppAccounts, getAppEditorId, getAppAccountEditorId],
  (apps, appAccounts, appId, appAccountId) => {
    const id = appId && apps[appId]?.id;
    if (!id || !appAccountId) return null;

    const options = appAccounts[appId]?.find(a => a.id === appAccountId)?.options ?? null;

    return options;
  }
);

export function getAppAccountDraftOptions<O = unknown>(state: IState): O {
  return state.apps.draft.options as O;
}

export const readAppImportWizardState = (state: IState) => state.apps.import;

const getAllImportEntities = (state: IState) =>
  importEntityAdapter.getSelectors().selectAll(state.apps.import.entities);

export const getImportWizardState = createSelector(
  [
    readAppImportWizardState,
    getAllImportEntities,
    getAppAccountEditorId,
    getAppAccountOptions,
    getAppsEntities,
    getAppEditorId,
  ],
  ({ selectedEntities, status }, entities, appAccountId, options, apps, appId) => {
    let appDisplayName = '';
    if (appId) {
      const app = apps[appId];
      appDisplayName = app.display_name;
    }

    return { entities, selectedEntities, options, status, appAccountId, appDisplayName };
  }
);

export const getGoogleCalendarSubscriptions = createSelector(
  [getAppAccounts, getAppSubscriptions, getAppsEntities],
  (appAccounts, appSubscriptions, apps) => {
    // Find Google Calendar app
    const googleCalendarApp = Object.values(apps).find(app => app.id === GOOGLE_CALENDAR);
    if (!googleCalendarApp) {
      return { subscriptions: [], isLoaded: false, hasAccounts: false };
    }

    // Get appAccounts for this app
    const googleCalendarAccounts = appAccounts[googleCalendarApp.app_id] || [];
    if (googleCalendarAccounts.length === 0) {
      return { subscriptions: [], isLoaded: false, hasAccounts: false };
    }

    // Check if subscriptions are loaded for all accounts
    const hasSubscriptionsData = googleCalendarAccounts.every(account =>
      Object.hasOwn(appSubscriptions, account.id)
    );

    // Get subscriptions for all accounts
    const subscriptions = googleCalendarAccounts.flatMap(
      appAccount => appSubscriptions[appAccount.id] || []
    );

    return {
      subscriptions,
      isLoaded: hasSubscriptionsData,
      hasAccounts: googleCalendarAccounts.length > 0,
    };
  }
);
